import { create } from 'zustand';

const useBoardStore = create((set, get) => ({
  // Board state
  boardId: '',
  stickers: [],
  connectors: [],
  
  // UI state
  selectedSticker: null,
  selectedConnector: null,
  isConnecting: false,
  connectingFrom: null,
  
  // WebSocket
  ws: null,
  isConnected: false,
  isReconnecting: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectDelay: 1000,
  heartbeatInterval: null,
  reconnectTimeout: null,
  
  // Actions
  setBoardState: (boardState) => set({
    boardId: boardState.board_id,
    stickers: boardState.stickers || [],
    connectors: boardState.connectors || []
  }),
  
  setSelectedSticker: (stickerId) => set({ selectedSticker: stickerId }),
  
  setSelectedConnector: (connectorId) => set({ selectedConnector: connectorId }),
  
  setConnecting: (isConnecting, fromId = null) => set({
    isConnecting,
    connectingFrom: fromId
  }),
  
  // WebSocket actions
  connectWebSocket: (boardId) => {
    const state = get();
    
    // Clear existing connections and timers
    if (state.ws) {
      state.ws.close();
    }
    if (state.heartbeatInterval) {
      clearInterval(state.heartbeatInterval);
    }
    if (state.reconnectTimeout) {
      clearTimeout(state.reconnectTimeout);
    }
    
    // Determine WebSocket URL based on environment
    const isLocalhost = window.location.hostname === 'localhost';
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    
    let wsUrl;
    if (isLocalhost) {
      // Check if we're running through proxy (port 80) or direct (port 3000)
      const isProxy = window.location.port === '80' || window.location.port === '';
      if (isProxy) {
        // Through proxy (Traefik or nginx)
        wsUrl = `ws://localhost/ws/${boardId}`;
      } else {
        // Direct development
        wsUrl = `ws://localhost:8080/ws/${boardId}`;
      }
    } else {
      // Production - check if using separate API domain or same domain with proxy
      const useSeparateApiDomain = true; // Set to false if using simple docker-compose-simple-prod.yml
      
      if (useSeparateApiDomain) {
        // Traefik setup with separate API subdomain
        wsUrl = `${wsProtocol}//api.prostodoska.ru/ws/${boardId}`;
      } else {
        // Simple production with nginx proxy on same domain
        wsUrl = `${wsProtocol}//${window.location.host}/ws/${boardId}`;
      }
    }
    
    console.log('Connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      set({ 
        isConnected: true, 
        isReconnecting: false,
        reconnectAttempts: 0,
        ws
      });
      
      // Start heartbeat
      const heartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, 30000); // Ping every 30 seconds
      
      set({ heartbeatInterval: heartbeat });
    };
    
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'board_update') {
          get().setBoardState(message.payload);
        } else if (message.type === 'pong') {
          // Heartbeat response received
          console.log('Heartbeat received');
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    ws.onclose = (event) => {
      console.log('WebSocket disconnected', event.code, event.reason);
      const currentState = get();
      
      set({ 
        isConnected: false,
        ws: null
      });
      
      // Clear heartbeat
      if (currentState.heartbeatInterval) {
        clearInterval(currentState.heartbeatInterval);
        set({ heartbeatInterval: null });
      }
      
      // Attempt reconnection if not manually closed
      if (event.code !== 1000 && currentState.reconnectAttempts < currentState.maxReconnectAttempts) {
        get().attemptReconnect(boardId);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    set({ boardId });
  },

  attemptReconnect: (boardId) => {
    const state = get();
    
    if (state.isReconnecting || state.reconnectAttempts >= state.maxReconnectAttempts) {
      return;
    }
    
    set({ 
      isReconnecting: true,
      reconnectAttempts: state.reconnectAttempts + 1
    });
    
    const delay = state.reconnectDelay * Math.pow(2, state.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${state.reconnectAttempts}/${state.maxReconnectAttempts})`);
    
    const timeout = setTimeout(() => {
      console.log(`Reconnecting... (attempt ${get().reconnectAttempts})`);
      get().connectWebSocket(boardId);
    }, delay);
    
    set({ reconnectTimeout: timeout });
  },

  disconnect: () => {
    const state = get();
    
    if (state.ws) {
      state.ws.close(1000, 'Manual disconnect');
    }
    
    if (state.heartbeatInterval) {
      clearInterval(state.heartbeatInterval);
    }
    
    if (state.reconnectTimeout) {
      clearTimeout(state.reconnectTimeout);
    }
    
    set({
      ws: null,
      isConnected: false,
      isReconnecting: false,
      reconnectAttempts: 0,
      heartbeatInterval: null,
      reconnectTimeout: null
    });
  },
  
  sendMessage: (message) => {
    const { ws, isConnected } = get();
    if (ws && isConnected) {
      ws.send(JSON.stringify(message));
    }
  },
  
  createSticker: (position, options = {}) => {
    get().sendMessage({
      action: 'sticker_create',
      payload: {
        position,
        text: '',
        color: options.color || '#fff3cd',
        size: options.size || { width: 120, height: 80 }
      }
    });
  },
  
  updateSticker: (stickerId, updates) => {
    get().sendMessage({
      action: 'sticker_update',
      payload: {
        id: stickerId,
        ...updates
      }
    });
  },
  
  createConnector: (fromId, toId) => {
    get().sendMessage({
      action: 'connector_create',
      payload: {
        from_id: fromId,
        to_id: toId
      }
    });
  },

  loadBoardData: (boardData) => {
    get().sendMessage({
      action: 'board_load',
      payload: boardData
    });
  },

  deleteConnector: (connectorId) => {
    get().sendMessage({
      action: 'connector_delete',
      payload: {
        id: connectorId
      }
    });
    // Clear selection
    set({ selectedConnector: null });
  }
}));

export default useBoardStore;
